import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { version } from '@@/package.json';

export default defineNuxtPlugin({
  name: 'observability',
  hooks: {
    'app:created'() {
      const nuxtApp = useNuxtApp();
      const hasDatadogLogEnabled = !!(nuxtApp.$config.public.datadog.logs.clientToken);
      if (hasDatadogLogEnabled) {
        const errorToDiscard = [
          `https://content.hotjar.io/`,
          `chrome-extension://`,
          `Uncaught "ResizeObserver loop completed with undelivered notifications."`,
        ] as const;

        datadogLogs.init({
          clientToken: nuxtApp.$config.public.datadog.logs.clientToken,
          service: 'website',
          site: `datadoghq.eu`,
          env: nuxtApp.$config.public.tracking.environment,
          version,
          forwardErrorsToLogs: true,
          forwardConsoleLogs: [`warn`, `error`],
          forwardReports: `all`,
          sessionSampleRate: parseSampleRate(nuxtApp.$config.public.datadog.logs.sessionSampleRate, 100),
          // Should be matching with RUM configuration
          // https://docs.datadoghq.com/fr/real_user_monitoring/browser/setup/client/?tab=rum
          sessionPersistence: `local-storage`,
          // https://docs.datadoghq.com/logs/log_collection/javascript/#discard-specific-logs
          beforeSend: (log) => {
            const shouldDiscard = errorToDiscard.some(error => log.message.includes(error));
            if (shouldDiscard) return false;
            return true;
          },
        });
      };
      // datadogLogs.setGlobalContextProperty(`application`, `playiad`);
      // datadogLogs.logger.setLevel(import.meta.env.VITE_CORE__DATADOG_LOG_LEVEL);

      // https://app.datadoghq.eu/rum/application/bdd61b95-5562-4b2a-a68f-12ef50e640bb/manage

      const hasDatadogRumEnabled = !!(nuxtApp.$config.public.datadog.rum.applicationId && nuxtApp.$config.public.datadog.rum.clientToken);
      if (hasDatadogRumEnabled) {
        const { defaultPrivacyLevel: configDefaultPrivacyLevel } = nuxtApp.$config.public.datadog.rum;
        const defaultPrivacyLevel = configDefaultPrivacyLevel === `allow`
          ? `allow`
          : configDefaultPrivacyLevel === `mask`
            ? `mask`
            : `mask-user-input`;
        datadogRum.init({
          applicationId: nuxtApp.$config.public.datadog.rum.applicationId,
          clientToken: nuxtApp.$config.public.datadog.rum.clientToken,
          site: `datadoghq.eu`,
          service: 'website',
          allowedTracingUrls: [/https:\/\/.*www\.iadfrance\.fr/],
          env: nuxtApp.$config.public.tracking.environment,
          version,
          sessionSampleRate: parseSampleRate(nuxtApp.$config.public.datadog.rum.sessionSampleRate, 100),
          sessionReplaySampleRate: parseSampleRate(nuxtApp.$config.public.datadog.rum.sessionReplaySampleRate, 20),
          trackResources: true,
          trackLongTasks: true,
          trackUserInteractions: true,
          // https://docs.datadoghq.com/real_user_monitoring/session_replay/browser/privacy_options/
          defaultPrivacyLevel,
          // https://docs.datadoghq.com/fr/real_user_monitoring/browser/setup/client/?tab=rum
          sessionPersistence: `local-storage`,
        });
      }
    },
  },
});

// Between 0 and 100
function parseSampleRate(value: unknown, fallback: number): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? fallback : parsedValue;
  };
  return fallback;
}
